import React, { useState, useEffect } from 'react';
import { Linkedin, Link as LinkIcon, CheckCircle, AlertCircle, Loader2, Mail, Sparkles, Puzzle, Copy } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { supabase } from '../../../lib/supabaseClient';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Integrations = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [linkedinSettings, setLinkedinSettings] = useState({
    enabled: false,
    connected: false,
    company_page_id: '',
    access_token: '',
  });

  const [gmailSettings, setGmailSettings] = useState({
    connected: false,
    email_address: null,
    connecting: false
  });

  const [signature, setSignature] = useState('');
  const [signatureLoading, setSignatureLoading] = useState(false);

  const [openaiSettings, setOpenaiSettings] = useState({
    api_key: '',
    connected: false
  });
  const [openaiSaving, setOpenaiSaving] = useState(false);

  const [extensionToken, setExtensionToken] = useState({
    hasToken: false,
    tokenPrefix: null,
    createdAt: null,
  });
  const [generatedToken, setGeneratedToken] = useState(null);
  const [extensionBusy, setExtensionBusy] = useState(false);

  useEffect(() => {
    fetchLinkedInSettings();
    fetchGmailStatus();
    fetchOpenAISettings();
    fetchExtensionTokenStatus();

    // Check for OAuth callback success/error
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'gmail_connected') {
      setMessage({ type: 'success', text: 'Gmail connected successfully!' });
      fetchGmailStatus();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('error')) {
      const error = urlParams.get('error');
      setMessage({ type: 'error', text: `Gmail connection failed: ${error}` });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchLinkedInSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setMessage({ type: 'error', text: 'Not authenticated' });
        return;
      }

      // Fetch LinkedIn settings from database
      const { data, error } = await supabase
        .from('linkedin_integrations')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      // Handle different error scenarios
      if (error) {
        // PGRST116 = no rows returned (this is fine, user hasn't set up LinkedIn yet)
        if (error.code === 'PGRST116') {
          // No existing settings, just use defaults
          return;
        }

        // 42P01 or PGRST204 = table doesn't exist
        if (error.code === '42P01' || error.code === 'PGRST204' || error.message?.includes('relation') || error.message?.includes('does not exist')) {
          setMessage({
            type: 'info',
            text: 'LinkedIn integration tables not found. Please run the database migration: database/create_linkedin_integration.sql in Supabase SQL Editor.'
          });
          return;
        }

        // Other errors
        throw error;
      }

      if (data) {
        setLinkedinSettings({
          enabled: data.enabled || false,
          connected: !!data.access_token,
          company_page_id: data.company_page_id || '',
          access_token: data.access_token || '',
        });
      }
    } catch (error) {
      console.error('Error fetching LinkedIn settings:', error);
      setMessage({ type: 'error', text: `Failed to load LinkedIn settings: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setLinkedinSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleEnabled = () => {
    setLinkedinSettings(prev => ({
      ...prev,
      enabled: !prev.enabled
    }));
  };

  const handleConnect = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // In production, this would initiate LinkedIn OAuth flow
      // For now, we'll show a placeholder message
      setMessage({
        type: 'info',
        text: 'LinkedIn OAuth integration will be configured in production. For now, manually enter your credentials below.'
      });

    } catch (error) {
      console.error('Error connecting to LinkedIn:', error);
      setMessage({ type: 'error', text: 'Failed to connect to LinkedIn' });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your LinkedIn account?')) {
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('linkedin_integrations')
        .update({
          access_token: null,
          company_page_id: null,
          enabled: false
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setLinkedinSettings(prev => ({
        ...prev,
        connected: false,
        company_page_id: '',
        access_token: '',
        enabled: false
      }));

      setMessage({ type: 'success', text: 'LinkedIn account disconnected successfully' });
    } catch (error) {
      console.error('Error disconnecting LinkedIn:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect LinkedIn account' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Upsert LinkedIn settings
      const { error } = await supabase
        .from('linkedin_integrations')
        .upsert({
          user_id: user.id,
          enabled: linkedinSettings.enabled,
          company_page_id: linkedinSettings.company_page_id,
          access_token: linkedinSettings.access_token,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        // Check if table doesn't exist
        if (error.code === '42P01' || error.code === 'PGRST204' || error.message?.includes('relation') || error.message?.includes('does not exist')) {
          throw new Error('LinkedIn integration tables not found. Please run the database migration first.');
        }
        throw error;
      }

      // Update connected status based on whether access token is set
      setLinkedinSettings(prev => ({
        ...prev,
        connected: !!linkedinSettings.access_token
      }));

      setMessage({ type: 'success', text: 'LinkedIn settings saved successfully' });
    } catch (error) {
      console.error('Error saving LinkedIn settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save LinkedIn settings' });
    } finally {
      setSaving(false);
    }
  };

  // Gmail Integration Functions
  const fetchGmailStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/gmail/status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGmailSettings({
          connected: data.connected,
          email_address: data.integration?.email_address || null,
          connecting: false
        });

        // Fetch signature if connected
        if (data.connected) {
          fetchSignature();
        }
      }
    } catch (error) {
      console.error('Error fetching Gmail status:', error);
    }
  };

  const fetchSignature = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/gmail/signature`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSignature(data.signature_html || '');
      }
    } catch (error) {
      console.error('Error fetching signature:', error);
    }
  };

  const handleSaveSignature = async () => {
    try {
      setSignatureLoading(true);
      setMessage(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      console.log('Saving signature...', { length: signature?.length });

      const response = await fetch(`${API_URL}/gmail/signature`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          signature_html: DOMPurify.sanitize(signature)
        })
      });

      const data = await response.json();
      console.log('Signature save response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save signature');
      }

      setMessage({ type: 'success', text: 'Email signature saved successfully' });
    } catch (error) {
      console.error('Error saving signature:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save signature' });
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      setGmailSettings(prev => ({ ...prev, connecting: true }));
      setMessage(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call backend to initiate OAuth
      const response = await fetch(`${API_URL}/gmail/auth/initiate`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to initiate Gmail connection');
      }

      const data = await response.json();

      // Redirect to Google OAuth
      window.location.href = data.authUrl;

    } catch (error) {
      console.error('Error connecting Gmail:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to connect Gmail' });
      setGmailSettings(prev => ({ ...prev, connecting: false }));
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Gmail account?')) {
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/gmail/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect Gmail');
      }

      setGmailSettings({
        connected: false,
        email_address: null,
        connecting: false
      });

      setMessage({ type: 'success', text: 'Gmail disconnected successfully' });
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to disconnect Gmail' });
    } finally {
      setSaving(false);
    }
  };

  // OpenAI Integration Functions
  const fetchOpenAISettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('integrations')
        .select('settings')
        .eq('user_id', user.id)
        .eq('integration_type', 'openai')
        .maybeSingle();

      if (!error && data?.settings) {
        setOpenaiSettings({
          api_key: data.settings.api_key || '',
          connected: !!data.settings.api_key
        });
      }
    } catch (error) {
      console.error('Error fetching OpenAI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOpenAI = async () => {
    try {
      setOpenaiSaving(true);
      setMessage(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Upsert OpenAI integration
      const { error } = await supabase
        .from('integrations')
        .upsert({
          user_id: user.id,
          integration_type: 'openai',
          enabled: !!openaiSettings.api_key,
          settings: {
            api_key: openaiSettings.api_key
          }
        }, {
          onConflict: 'user_id,integration_type'
        });

      if (error) throw error;

      setOpenaiSettings(prev => ({
        ...prev,
        connected: !!openaiSettings.api_key
      }));

      setMessage({ type: 'success', text: 'OpenAI settings saved successfully' });
    } catch (error) {
      console.error('Error saving OpenAI settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save OpenAI settings' });
    } finally {
      setOpenaiSaving(false);
    }
  };

  // Browser extension token functions
  const fetchExtensionTokenStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/extension/token`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setExtensionToken({
          hasToken: data.hasToken,
          tokenPrefix: data.tokenPrefix,
          createdAt: data.createdAt,
        });
      }
    } catch (error) {
      console.error('Error fetching extension token status:', error);
    }
  };

  const handleGenerateExtensionToken = async () => {
    try {
      setExtensionBusy(true);
      setMessage(null);
      setGeneratedToken(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/extension/token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate token');
      }

      setGeneratedToken(data.token);
      await fetchExtensionTokenStatus();
    } catch (error) {
      console.error('Error generating extension token:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to generate extension token' });
    } finally {
      setExtensionBusy(false);
    }
  };

  const handleRevokeExtensionToken = async () => {
    if (!window.confirm('Revoke the browser extension token? Any extension using it will stop working immediately.')) {
      return;
    }

    try {
      setExtensionBusy(true);
      setMessage(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/extension/token`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to revoke token');
      }

      setGeneratedToken(null);
      await fetchExtensionTokenStatus();
      setMessage({ type: 'success', text: 'Extension token revoked' });
    } catch (error) {
      console.error('Error revoking extension token:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to revoke extension token' });
    } finally {
      setExtensionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Integrations</h3>
        <p className="text-sm text-muted-foreground">
          Connect and manage third-party integrations
        </p>
      </div>

      {message && (
        <Alert className={
          message.type === 'error' ? 'border-destructive' :
          message.type === 'success' ? 'border-green-500' :
          'border-blue-500'
        }>
          {message.type === 'error' && <AlertCircle className="h-4 w-4" />}
          {message.type === 'success' && <CheckCircle className="h-4 w-4" />}
          <AlertDescription className={
            message.type === 'error' ? 'text-destructive' :
            message.type === 'success' ? 'text-green-700' :
            'text-blue-700'
          }>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-[#0077B5] rounded-lg flex items-center justify-center">
                <Linkedin className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>LinkedIn</CardTitle>
                <CardDescription>
                  Auto-post RFQs to your LinkedIn company page
                </CardDescription>
              </div>
            </div>
            {linkedinSettings.connected && (
              <Badge variant="success" className="bg-green-100 text-green-700 border-green-300">
                <CheckCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between py-3 border-b">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">
                Enable LinkedIn Integration
              </Label>
              <p className="text-sm text-muted-foreground">
                Automatically post new RFQs to LinkedIn when marked as visible
              </p>
            </div>
            <button
              onClick={handleToggleEnabled}
              disabled={!linkedinSettings.connected}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                linkedinSettings.enabled ? 'bg-primary' : 'bg-muted'
              } ${!linkedinSettings.connected ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  linkedinSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* OAuth Connection (for production) */}
          <div className="space-y-3">
            <Alert>
              <LinkIcon className="h-4 w-4" />
              <AlertDescription>
                For production, use OAuth to connect. For development/testing, manually enter credentials below.
              </AlertDescription>
            </Alert>
            {!linkedinSettings.connected ? (
              <Button onClick={handleConnect} disabled={saving} className="w-full">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Linkedin className="h-4 w-4 mr-2" />
                    Connect via OAuth (Production)
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleDisconnect} variant="outline" disabled={saving} className="w-full">
                Disconnect LinkedIn Account
              </Button>
            )}
          </div>

          {/* Manual Configuration (always visible for development/testing) */}
          <div className="space-y-4 pt-4 border-t">
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Manual Configuration</h4>
              <p className="text-xs text-muted-foreground">
                For development and testing. Enter your LinkedIn API credentials manually.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_page_id">
                Company Page ID
              </Label>
              <Input
                id="company_page_id"
                value={linkedinSettings.company_page_id}
                onChange={(e) => handleInputChange('company_page_id', e.target.value)}
                placeholder="12345678"
              />
              <p className="text-xs text-muted-foreground">
                Your LinkedIn company page ID (numeric only). Find it in your Company Page URL or Admin dashboard.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_token">
                Access Token
              </Label>
              <Input
                id="access_token"
                type="password"
                value={linkedinSettings.access_token}
                onChange={(e) => handleInputChange('access_token', e.target.value)}
                placeholder="Enter LinkedIn API access token"
              />
              <p className="text-xs text-muted-foreground">
                OAuth access token for LinkedIn API. See LINKEDIN_INTEGRATION_SETUP.md for how to get this.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gmail Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-red-500 rounded-lg flex items-center justify-center">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Gmail</CardTitle>
                <CardDescription>
                  Draft and send candidate emails directly from your Gmail
                </CardDescription>
              </div>
            </div>
            {gmailSettings.connected && (
              <Badge variant="success" className="bg-green-100 text-green-700 border-green-300">
                <CheckCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {gmailSettings.connected && gmailSettings.email_address && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Mail className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-700">
                Connected to: <strong>{gmailSettings.email_address}</strong>
              </span>
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Features</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Draft candidate outreach emails in your Gmail</li>
                <li>Send emails directly from the matching interface</li>
                <li>Receive notifications when candidates reply</li>
                <li>All emails sent via your connected Gmail account</li>
              </ul>
            </div>

            {!gmailSettings.connected ? (
              <Button
                onClick={handleConnectGmail}
                disabled={gmailSettings.connecting}
                className="w-full"
              >
                {gmailSettings.connecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Connect Gmail Account
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleDisconnectGmail}
                variant="outline"
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect Gmail Account'
                )}
              </Button>
            )}
          </div>

          {/* Email Signature */}
          {gmailSettings.connected && (
            <div className="space-y-3 pt-4 border-t">
              <div className="space-y-1">
                <Label className="text-base font-medium">Email Signature</Label>
                <p className="text-sm text-muted-foreground">
                  Your signature will be automatically appended to all Gmail drafts
                </p>
              </div>

              <div className="space-y-3">
                {/* HTML Input */}
                <div>
                  <Label htmlFor="signature-html" className="text-sm font-medium">
                    Signature HTML
                  </Label>
                  <Textarea
                    id="signature-html"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Paste your Gmail signature HTML here..."
                    rows={8}
                    className="font-mono text-xs mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Tip: Copy your signature from Gmail → Settings → Signature
                  </p>
                </div>

                {/* Preview */}
                {signature && (
                  <div>
                    <Label className="text-sm font-medium">Preview</Label>
                    <div
                      className="mt-2 border rounded-md p-4 bg-white"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(signature) }}
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSignature}
                  disabled={signatureLoading}
                  size="sm"
                >
                  {signatureLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Signature'
                  )}
                </Button>
              </div>
            </div>
          )}

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Gmail integration uses OAuth 2.0 for secure access. Your credentials are encrypted and can be revoked at any time.
              We only access your Gmail to draft and send emails - we never read or store your existing emails.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* OpenAI Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>OpenAI</CardTitle>
                <CardDescription>
                  AI-powered candidate matching and RFQ profile generation
                </CardDescription>
              </div>
            </div>
            {openaiSettings.connected && (
              <Badge variant="success" className="bg-green-100 text-green-700 border-green-300">
                <CheckCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Features</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>AI-generated match profiles for RFQs</li>
              <li>Automatic extraction of requirements from RFQ descriptions</li>
              <li>Intelligent skill matching and candidate recommendations</li>
              <li>Natural language processing for better search results</li>
            </ul>
          </div>

          <div className="space-y-3 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="openai-api-key">OpenAI API Key</Label>
              <Input
                id="openai-api-key"
                type="password"
                value={openaiSettings.api_key}
                onChange={(e) => setOpenaiSettings({ ...openaiSettings, api_key: e.target.value })}
                placeholder="sk-..."
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Get your API key from{' '}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  OpenAI Platform
                </a>
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveOpenAI}
                disabled={openaiSaving || !openaiSettings.api_key}
              >
                {openaiSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save API Key'
                )}
              </Button>
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Your OpenAI API key is encrypted and stored securely. It's used only for AI matching features within this application.
              Usage costs depend on your OpenAI account. You can revoke access by removing the key here.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Browser Extension */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-slate-700 rounded-lg flex items-center justify-center">
                <Puzzle className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Browser Extension</CardTitle>
                <CardDescription>
                  Optional. See applicant/match counts on BuyICT opportunity pages - read-only.
                </CardDescription>
              </div>
            </div>
            {extensionToken.hasToken && (
              <Badge variant="success" className="bg-green-100 text-green-700 border-green-300">
                <CheckCircle className="h-3 w-3 mr-1" />
                Token active
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Install the extension from <code>extension/</code> in the repo (unpacked, "Load unpacked" in
            Chrome's extension settings), then paste a token here into its popup. The extension only ever
            makes one read-only call per BuyICT page it recognizes - it can't post, apply, or change
            anything on your behalf.
          </p>

          {extensionToken.hasToken && !generatedToken && (
            <div className="text-sm p-3 bg-muted/40 rounded-lg">
              Token <code>{extensionToken.tokenPrefix}...</code> generated{' '}
              {extensionToken.createdAt ? new Date(extensionToken.createdAt).toLocaleDateString() : ''}.
              Regenerating replaces it immediately - the old one stops working.
            </div>
          )}

          {generatedToken && (
            <Alert className="border-amber-400">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 space-y-2">
                <p>Copy this now - it won't be shown again:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border rounded px-2 py-1.5 break-all">{generatedToken}</code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(generatedToken)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button onClick={handleGenerateExtensionToken} disabled={extensionBusy} variant={extensionToken.hasToken ? 'outline' : 'default'}>
              {extensionBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {extensionToken.hasToken ? 'Regenerate token' : 'Generate token'}
            </Button>
            {extensionToken.hasToken && (
              <Button onClick={handleRevokeExtensionToken} disabled={extensionBusy} variant="outline">
                Revoke
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          LinkedIn posting functionality requires a LinkedIn Developer App with appropriate permissions.
          The OAuth flow and API integration will be fully implemented in production.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default Integrations;
